import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';

import { FormData, FormProviderService, PreventDoubleSubmission } from '@fry/lib/forms';
import { RolesStore } from '@fry/lib/roles';
import { map, switchMap, tap } from 'rxjs/operators';
import { User, UsersStore } from '@fry/lib/users';
import { forkJoin, Observable, throwError } from 'rxjs';
import { SecurityService } from '@fry/lib/security';
import { SelectableListMapping } from '@fry/components/common/selectable-list/selectable-list.mapping';
import { SYSTEM_ROLE_ID } from '@fry/lib/roles/roles.system';


@Injectable()
export class UserEditRolesForm {
  private formData: FormData;

  constructor(
      private provider: FormProviderService,
      private users: UsersStore,
      private roles: RolesStore,
      private security: SecurityService,
  ) { }

  private getRolesField() {
    return this.roles.all().pipe(
        map(roles => {
          const opts = roles.map(role => {
            return { id: role.doc._id, title: role.doc.title, description: role.doc.description };
          });
          return [{
                id: 'roles', title: $localize `Roles`, initial: [],
                options: {
                    categories: SelectableListMapping.create([{items: opts, id: 'roles'}]),
                },
                widget: { type: 'selectableList', autocomplete: '' },
                validators: [Validators.required],
            }];
        }));
  }

  private getOrgAdminField() {
      return this.security.hasRole(SYSTEM_ROLE_ID.ADMIN).pipe(
          map(res => {
              if (!res) {
                  return [];
              }

              return[{
                    id: 'isOrgSuperUser', title: $localize `Organisation admin`, initial: false,
                    widget: { type: 'boolean' },
                    validators: [],
            }];
          })
      );
  }

  getFields() {
      return forkJoin(
          this.getOrgAdminField(),
          this.getRolesField(),
      ).pipe(
          map(res => [].concat(...res))
      );
  }

  init(user: User): Observable<FormData> {
    return this.getFields().pipe(
        switchMap(fields => this.provider.getFormDataWith(fields, user.doc)),
        tap(formData => this.formData = formData)
      );
  }

  @PreventDoubleSubmission()
  submit(user: User): Observable<User> {
      if (this.formData.invalid) {
          return throwError($localize `Please review the forn`);
      }

      const value = this.formData.getValue();
      return this.users.updateRoles(user, value);
  }

  get pristine() {
      if (!this.formData) {
          return true;
      }
      return this.formData.pristine;
  }
}
